import React, { useCallback, useEffect, useState } from "react";
import { Loader2, RefreshCw, CheckCircle2, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../../lib/api";
import { apiErr } from "../../../lib/apiErr";

const SIZE = 12;

export function AvatarPicker({ provider, value, onPick, readOnly }) {
  const la = provider === "liveavatar";
  const [scope, setScope] = useState("mine");
  const [page, setPage] = useState(1);
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setBusy(true);
    try { const r = await api.get("/platform/support-agents/avatars", { params: { provider, page, page_size: SIZE, ...(la && { mine: scope === "mine" }) } }); setD(r.data); }
    catch (e) { toast.error(apiErr(e, "Gagal memuat avatar")); setD({ items: [] }); } finally { setBusy(false); }
  }, [provider, page, scope, la]);
  useEffect(() => { if (d) load(); }, [page, scope]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div data-testid={`avatar-picker-${provider}`}>
      <div className="flex flex-wrap items-center gap-2">
        {la && <div className="seg">{[["mine", "Avatar saya"], ["public", "Publik"]].map(([k, l]) => <button key={k} type="button" onClick={() => { setScope(k); setPage(1); }} className={`seg-item ${scope === k ? "seg-active" : ""}`} data-testid={`avatar-scope-${provider}-${k}`}>{l}</button>)}</div>}
        <button type="button" onClick={load} disabled={busy} className="btn-soft inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-bold" data-testid={`avatar-load-${provider}`}>{busy ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} {d ? "Muat ulang" : "Pilih avatar"}</button>
      </div>
      {d && (
        <>
          <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4">
            {!d.items.length && <p className="col-span-full text-xs text-slate-400">Tidak ada avatar.</p>}
            {d.items.map((v) => {
              const sel = v.id === value; const ok = ["READY", "ACTIVE"].includes(String(v.status || "").toUpperCase());
              return (
                <button key={v.id} type="button" disabled={readOnly} onClick={() => onPick(v)} data-testid={`avatar-${provider}-${v.id}`} className={`relative overflow-hidden rounded-xl border bg-white text-left transition ${sel ? "border-brand ring-2 ring-brand" : "border-line hover:border-brand/40"}`}>
                  {sel && <span className="absolute right-1 top-1 z-10 rounded-full bg-brand p-0.5 text-white"><CheckCircle2 size={12} /></span>}
                  <div className="aspect-square bg-slate-100">{v.preview ? <img src={v.preview} alt="" className="h-full w-full object-cover" loading="lazy" /> : <ImageIcon size={18} className="m-auto mt-8 text-slate-300" />}</div>
                  <div className="p-1.5"><p className="truncate text-[11px] font-semibold text-slate-800">{v.name || v.id}</p><span className={`text-[9px] font-bold ${ok ? "text-emerald-600" : "text-amber-600"}`}>{v.status || "?"}</span></div>
                </button>
              );
            })}
          </div>
          {(page > 1 || d.has_more) && (
            <div className="mt-2 flex items-center justify-end gap-2 text-xs">
              <button type="button" disabled={page === 1 || busy} onClick={() => setPage((p) => p - 1)} className="btn-soft rounded-lg px-2 py-1 disabled:opacity-40" data-testid={`avatar-prev-${provider}`}>‹</button>
              <span className="text-slate-500">Hal. {page}</span>
              <button type="button" disabled={!d.has_more || busy} onClick={() => setPage((p) => p + 1)} className="btn-soft rounded-lg px-2 py-1 disabled:opacity-40" data-testid={`avatar-next-${provider}`}>›</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
